import type { ReactNode } from "react";
import type { NormalizedStack, NormalizedTool } from "@/lib/toolStack";
import type { NormalizedSupport } from "@/lib/orgSupport";
import { toolCategoryMeta } from "@/lib/toolCategories";
import { ToolTags } from "./SpecTag";

/**
 * "How it runs" as a spec sheet (spec 2026-10-15 §5). Order is fixed:
 * Built in-house → Daily drivers → library categories → support. A block with
 * nothing in it is absent. No logos, no ratings, nothing hover-only. The
 * in-house heading never names an organization: an office can inherit its
 * parent's tools. Inputs are already normalized, so priced text is gone.
 */
interface Props {
  stack: NormalizedStack;
  support?: NormalizedSupport;
}

const CARD = "rounded-[8px] border border-border bg-card";

function BlockHeading({ children }: { children: ReactNode }) {
  return <h3 className="mb-3 text-[15px] font-semibold text-ink-900">{children}</h3>;
}

function CategoryIcon({ slug }: { slug: string }) {
  const Icon = toolCategoryMeta(slug).icon;
  return <Icon aria-hidden="true" className="h-4 w-4 shrink-0 text-primary" strokeWidth={2} />;
}

function Row({ label, icon, children }: { label: string; icon?: ReactNode; children: ReactNode }) {
  return (
    <div className="grid gap-2 p-4 sm:grid-cols-[minmax(0,220px)_1fr] sm:gap-6">
      <div className="flex items-center gap-2 text-sm font-medium text-ink-900">
        {icon}
        <span>{label}</span>
      </div>
      <div className="min-w-0 text-sm text-ink-700">{children}</div>
    </div>
  );
}

function ToolLine({ tool }: { tool: NormalizedTool }) {
  return (
    <li>
      <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-medium text-ink-900">{tool.name}</span>
        <ToolTags tool={tool} />
      </span>
      {tool.note && <p className="mt-0.5 text-[13px] text-ink-500">{tool.note}</p>}
    </li>
  );
}

export default function OperationsSpecSheet({ stack, support }: Props) {
  const hasSupport = !!support && !support.isEmpty;
  if (stack.isEmpty && !hasSupport) return null;

  const elevated = stack.inHouse.length > 0 || stack.drivers.length > 0;

  return (
    <div className="flex flex-col gap-[22px]">
      {stack.inHouse.length > 0 && (
        <section>
          <BlockHeading>Built in-house</BlockHeading>
          <div className="grid gap-3 sm:grid-cols-2">
            {stack.inHouse.map((tool) => (
              <article key={tool.name} className={`${CARD} p-4`}>
                <p className="text-[15px] font-semibold text-ink-900">{tool.name}</p>
                <p className="mt-1 text-sm text-ink-700">{tool.note}</p>
                <ToolTags tool={tool} className="mt-3" />
              </article>
            ))}
          </div>
        </section>
      )}

      {stack.drivers.length > 0 && (
        <section>
          <BlockHeading>Daily drivers</BlockHeading>
          <div className="grid gap-3 sm:grid-cols-3">
            {stack.drivers.map((tool) => (
              <article key={tool.slug ?? tool.name} className={`${CARD} border-t-2 border-t-gold-highlight p-4`}>
                <div className="flex items-center gap-2 text-[13px] text-ink-500">
                  <CategoryIcon slug={tool.categorySlug} />
                  <span>{toolCategoryMeta(tool.categorySlug).name}</span>
                </div>
                <p className="mt-2 text-[17px] font-semibold text-ink-900">{tool.name}</p>
                {tool.note && <p className="mt-1 text-sm text-ink-700">{tool.note}</p>}
                <ToolTags tool={tool} className="mt-3" />
              </article>
            ))}
          </div>
        </section>
      )}

      {stack.categories.length > 0 && (
        <section>
          <BlockHeading>{elevated ? "The rest of the stack" : "The stack"}</BlockHeading>
          <div className={`${CARD} divide-y divide-border`}>
            {stack.categories.map((category) => (
              <Row key={category.slug} label={category.name} icon={<CategoryIcon slug={category.slug} />}>
                <ul className="flex flex-col gap-2">
                  {category.tools.map((tool) => (
                    <ToolLine key={tool.slug ?? tool.name} tool={tool} />
                  ))}
                </ul>
              </Row>
            ))}
          </div>
        </section>
      )}

      {hasSupport && (
        <section>
          <BlockHeading>The people behind the tools</BlockHeading>
          <div className={`${CARD} divide-y divide-border`}>
            {support!.transactionCoordinator && (
              <Row label="Transaction coordinator">{support!.tcDetails ?? "Provided"}</Row>
            )}
            {support!.adminAssistance && <Row label="Admin assistance">{support!.adminDetails ?? "Provided"}</Row>}
            {support!.marketingSupport.length > 0 && (
              <Row label="Marketing support">{support!.marketingSupport.join(", ")}</Row>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
